import { Box, Button, LinearProgress, Typography } from "@mui/material";
import React, { useState } from "react";
import { courseService } from "../../../../../services/course.service";
import useSnackBar from "../../../../../hooks/useSnackBar";
import useSuccessModal from "../../../../../hooks/useSuccessModal";
import CourseCreateStepOne from "./CourseCreateStepOne/CourseCreateStepOne";
import CourseCreateStepTwo from "./CourseCreateStepTwo/CourseCreateStepTwo";
import CourseCreateStepThree from "./CourseCreateStepThree/CourseCreateStepThree";
import { useQuery } from "@tanstack/react-query";
import Layout from "../../../../../shared/Layout";
import { useNavigate } from "react-router-dom";

interface CourseData {
  category_id: string;
  name: string;
  planned_time: string;
  subscribe: boolean;
}

interface FetchCourseCategories {
  id: string;
  name: string;
  subs: string[];
}

const useFetchCourseCategories = () => {
  return useQuery<FetchCourseCategories[], Error>({
    queryKey: ["categories"],
    queryFn: () =>
      courseService.getCourseCategories().then((response) => response.data),
  });
};

const CourseCreate = ({ close }: any) => {
  const navigate = useNavigate();
  const {
    data: categoriesData,
    isLoading,
    isError,
    error,
  } = useFetchCourseCategories();
  const { showSuccessModal, SuccessModal } = useSuccessModal();
  const { setSnackBar } = useSnackBar();
  const [step, setStep] = useState(1);
  const [courseData, setCourseData] = useState({
    category_id: "",
    name: "",
    planned_time: "",
    subscribe: false,
  });

  const renderStepContent = () => {
    const categories = categoriesData || [];
    switch (step) {
      case 1:
        return (
          <CourseCreateStepOne
            courseData={courseData}
            onDataChange={handleStepChange}
          />
        );
      case 2:
        return (
          <CourseCreateStepTwo
            courseData={courseData}
            onDataChange={handleStepChange}
            categories={categories}
            setStep={setStep}
          />
        );
      case 3:
        return (
          <CourseCreateStepThree
            courseData={courseData}
            setStep={setStep}
            createCourse={createCourse}
            handleStepChange={handleStepChange}
          />
        );
      default:
        return null;
    }
  };
  const handleStepChange = (stepData: any) => {
    setCourseData((prevValue) => ({
      ...prevValue,
      ...stepData,
    }));
    if (step < 3) {
      setStep(step + 1);
    }
  };
  const createCourse = (courseData: CourseData) => {
    const { subscribe, ...dataToSend } = courseData;
    courseService
      .createCourse(dataToSend)
      .then((response: any) => {
        showSuccessModal(
          "Курс успешно создан!",
          "Заполните обязательные поля и отправьте курс на проверку",
          () => navigate(`/panel/constructor/${response.data.id}`)
        );
      })
      .catch((error) => {
        console.error("Ошибка при создании курса:", error);
        setSnackBar({
          title: "Ошибка при создании курса!",
          isActive: true,
          status: "error",
        });
      });
  };

  if (isLoading) {
    return (
      <Box>
        <LinearProgress />
      </Box>
    );
  }
  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}>
        <Typography>Ошибка: {error?.message}</Typography>
      </Box>
    );
  }
  if (!categoriesData) {
    return (
      <Layout>
        <Box>Категории курсов не найдены</Box>
      </Layout>
    );
  }
  return (
    <Box>
      <SuccessModal />
      <div>
        <div className="course-create-header">
          <div className="course-create-header-info">
            <h4>Создание курса</h4>
            <div className="steps-count">Шаг {step} из 3</div>
          </div>
          <Button
            className="close-modal-btn"
            variant="outlined"
            onClick={() => close()}
            sx={{
              marginLeft: "auto",
            }}>
            Выйти
          </Button>
        </div>
        <div className="course-create-body">
          <div className="step-indicator">
            <div
              className={
                step >= 1 ? "step-indicator-item active" : "step-indicator-item"
              }></div>
            <div
              className={
                step >= 2 ? "step-indicator-item active" : "step-indicator-item"
              }></div>
            <div
              className={
                step >= 3 ? "step-indicator-item active" : "step-indicator-item"
              }></div>
          </div>
          <div>{renderStepContent()}</div>
        </div>
      </div>
    </Box>
  );
};

export default CourseCreate;
